import { Effect } from "effect";
import { useComposer } from "./react.tsx";
import {
  useMemo,
  useState,
  useRef,
  type KeyboardEvent,
  type DragEvent,
  type ChangeEvent
} from "react";
import {
  CHAT_ATTACHMENT_MIME_TYPES,
  type ChatAttachment
} from "@workerstation/shared";
import { IconButtonV2 } from "../ui/icon-button-v2.tsx";
import { Icon } from "../ui/icon.tsx";
import {
  createPromptInputInteractionState,
  transitionPromptInput,
  type PromptInputInteractionState,
} from "../opencode/prompt-machine.ts";

export type Attachment = ChatAttachment;

export interface PromptInputProps {
  scopeKey?: string;
  value: string;
  onChange: (value: string) => void;
  onSend: (
    text: string,
    attachments: Attachment[]
  ) => Effect.Effect<void | boolean>;
  onStop?: () => void;
  stopDisabled?: boolean;
  onAttachmentError?: (message: string) => void;
  disabled?: boolean;
  busy?: boolean;
  placeholder?: string;
}

export function PromptInput({
  scopeKey = "",
  value,
  onChange,
  onSend,
  onStop,
  stopDisabled = false,
  onAttachmentError,
  disabled = false,
  busy = false,
  placeholder = "Ask anything…"
}: PromptInputProps) {
  const [state, setState] = useState<PromptInputInteractionState>(createPromptInputInteractionState);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const send = useMemo(() => (text: string, attachments: readonly Attachment[]) => onSend(text, [...attachments]), [onSend]);
  const error = useMemo(() => (message: string) => Effect.sync(() => onAttachmentError?.(message)), [onAttachmentError]);
  const composer = useComposer({ scopeKey, value, disabled, busy, send, error });
  const { attachments, submitting } = composer.snapshot;
  const persistedAttachments = () => attachments.map(attachment => ({
    id: attachment.id, name: attachment.filename, type: attachment.mime.startsWith("image/") ? "image" as const : "file" as const
  }));
  const submit = () => composer.dispatch({ _tag: "Submit" });
  const enqueueFiles = (files: readonly File[]) => composer.dispatch({ _tag: "Attach", files });

  const handleTextChange = (newVal: string) => {
    onChange(newVal);
    const transition = transitionPromptInput(
      state,
      { type: "input.changed", value: newVal },
      { text: newVal, attachments: persistedAttachments() }
    );
    setState(transition.state);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submit();
    } else if (e.key === "Escape") {
      const transition = transitionPromptInput(
        state,
        { type: "key.down", key: "Escape", ctrl: false, composing: false, ids: [] },
        { text: value, attachments: persistedAttachments() }
      );
      setState(transition.state);
    }
  };

  const handleFileDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      enqueueFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      enqueueFiles(Array.from(e.target.files));
      e.target.value = "";
    }
  };

  const removeAttachment = (id: string) => composer.dispatch({ _tag: "Remove", id });

  return (
    <div
      data-component="prompt-input"
      data-mode={state.mode}
      data-drag={state.drag}
      className="flex flex-col rounded-lg border border-kumo-line bg-kumo-surface p-2 focus-within:border-sky-500"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleFileDrop}
    >
      {attachments.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1" data-testid="attachments-list">
          {attachments.map((att) => (
            <span
              key={att.id}
              className="flex items-center gap-1 rounded bg-kumo-fill px-2 py-0.5 text-[11px] text-kumo-default"
            >
              <Icon name={att.mime.startsWith("image/") ? "edit" : "folder"} size="small" />
              <span>{att.filename}</span>
              <button
                type="button"
                className="ml-1 text-kumo-subtle hover:text-rose-500"
                onClick={() => removeAttachment(att.id)}
                aria-label={`Remove ${att.filename}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div className="flex items-end gap-2">
        <textarea
          aria-label="Prompt input"
          className="h-16 min-w-0 flex-1 resize-none bg-transparent text-[13px] text-kumo-default outline-none"
          placeholder={placeholder}
          value={value}
          disabled={disabled || submitting}
          onChange={(e) => handleTextChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={(event) => {
            const files = Array.from(event.clipboardData.items).flatMap((item) => {
              if (item.kind !== "file") return [];
              const file = item.getAsFile();
              return file ? [file] : [];
            });
            if (files.length > 0) {
              event.preventDefault();
              enqueueFiles(files);
            }
          }}
        />
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          multiple
          accept={CHAT_ATTACHMENT_MIME_TYPES.join(",")}
          onChange={handleFileSelect}
        />
        <IconButtonV2
          size="small"
          variant="ghost"
          icon={<Icon name="folder" />}
          disabled={disabled || submitting}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Attach file"
          title="Attach file"
        />
        {busy ? (
          <button
            type="button"
            data-component="composer-send"
            aria-label="Stop generating"
            title="Stop generating"
            disabled={stopDisabled}
            onClick={onStop}
          >
            <span aria-hidden="true" data-slot="stop" />
          </button>
        ) : (
          <button
            type="button"
            data-component="composer-send"
            aria-label="Send message"
            title="Send message"
            disabled={disabled || submitting || !value.trim()}
            onClick={() => void submit()}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
              <path
                d="M8 13V3M8 3L4 7M8 3l4 4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
